import { useQuery } from '@tanstack/react-query'
import {
  ArrowRight, Check, Circle, Clapperboard, FileVideo2, Image, KeyRound,
  UserSquare2,
} from 'lucide-react'
import { api, ApiError } from '../api'
import type { Asset, Avatar, Brand, Job, Look, Role } from '../types'
import { atLeast } from '../types'
import { go, type Route } from '../router'
import { Button, ErrorText, Skeleton } from '../components/ui'
import { cn } from '../lib/utils'

type Credential = { provider: 'kie' | 'elevenlabs'; configured: boolean }

type SetupStep = {
  title: string
  description: string
  complete: boolean
  action: string
  to: Route
  icon: React.ComponentType<{ className?: string }>
  permission?: boolean
}

export function GettingStarted({ ws, role }: { ws: string; role?: Role }) {
  const credentials = useQuery({
    queryKey: ['credentials', ws],
    queryFn: () => api.get<Credential[]>(`/api/workspaces/${ws}/credentials`),
  })
  const brand = useQuery({
    queryKey: ['brand', ws],
    queryFn: async () => {
      try {
        return await api.get<Brand>(`/api/workspaces/${ws}/brand`)
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null
        throw error
      }
    },
  })
  const avatars = useQuery({
    queryKey: ['avatars', ws],
    queryFn: () => api.get<Avatar[]>(`/api/workspaces/${ws}/avatars`),
  })
  const jobs = useQuery({
    queryKey: ['jobs', ws],
    queryFn: () => api.get<Job[]>(`/api/workspaces/${ws}/jobs`),
  })
  const assets = useQuery({
    queryKey: ['assets', ws, brand.data?.id],
    queryFn: () => api.get<Asset[]>(
      `/api/workspaces/${ws}/brands/${brand.data!.id}/assets?kind=insert`),
    enabled: !!brand.data?.id,
  })
  const looks = useQuery({
    queryKey: ['getting-started-looks', ws, avatars.data?.map(a => a.id).join(',')],
    queryFn: async () => (await Promise.all(
      (avatars.data ?? []).map(a =>
        api.get<Look[]>(`/api/workspaces/${ws}/avatars/${a.id}/looks`)),
    )).flat(),
    enabled: !!avatars.data,
  })

  const loading = credentials.isLoading || brand.isLoading || avatars.isLoading || jobs.isLoading
    || (!!avatars.data && looks.isLoading) || (!!brand.data && assets.isLoading)
  const error = credentials.error || brand.error || avatars.error || jobs.error || assets.error || looks.error
  const hasKeys = ['kie', 'elevenlabs'].every(provider =>
    credentials.data?.some(key => key.provider === provider && key.configured))
  const hasBrand = !!brand.data && !!(
    brand.data.one_liner || brand.data.what_it_does || brand.data.key_benefits?.length)
  const hasAvatar = (avatars.data?.length ?? 0) > 0
  // Every avatar gets its source photo as an immediately usable starter look.
  // This step specifically asks for a generated look, whose frame has its own
  // storage key. The legacy `saved` flag is not used by the current save flow.
  const hasLook = looks.data?.some(look => {
    const avatar = avatars.data?.find(item => item.id === look.avatar_id)
    const original = avatar?.source_key || avatar?.closeup_key
    return !!original && look.start_frame_key !== original
  }) ?? false
  const hasDemo = (assets.data?.length ?? 0) > 0
  const hasVideo = (jobs.data?.length ?? 0) > 0
  const canEdit = atLeast(role, 'member')
  const canAdmin = atLeast(role, 'admin')

  const steps: SetupStep[] = [
    {
      title: 'Add API keys',
      description: 'Add KIE and ElevenLabs keys. This workspace never uses server fallback keys.',
      complete: hasKeys,
      action: hasKeys ? 'Review keys' : 'Add keys',
      to: { name: 'settings', section: 'keys' },
      icon: KeyRound,
      permission: canAdmin,
    },
    {
      title: 'Review brand context',
      description: 'Read the saved website, then check the product, audience, benefits and “Don’t mention” list.',
      complete: hasBrand,
      action: hasBrand ? 'Review context' : 'Set up context',
      to: { name: 'settings', section: 'brands', sub: hasBrand ? undefined : 'new' },
      icon: Image,
      permission: canEdit,
    },
    {
      title: 'Create an avatar',
      description: 'Add the person, voice, gender and age range used across generated scenes.',
      complete: hasAvatar,
      action: hasAvatar ? 'View avatars' : 'Add avatar',
      to: { name: 'settings', section: 'avatars', sub: hasAvatar ? undefined : 'new' },
      icon: UserSquare2,
      permission: canEdit,
    },
    {
      title: 'Generate a look',
      description: 'Choose the avatar’s environment, composition and outfit, then save a start frame.',
      complete: hasLook,
      action: 'Open avatars',
      to: { name: 'settings', section: 'avatars' },
      icon: Image,
      permission: canEdit,
    },
    {
      title: 'Add a product demo',
      description: 'Upload the product footage shown between the opening and CTA, covering the task, feature, solution or use case.',
      complete: hasDemo,
      action: hasDemo ? 'View demos' : 'Add demo',
      to: { name: 'settings', section: 'assets' },
      icon: FileVideo2,
      permission: canEdit,
    },
    {
      title: 'Create your first video',
      description: 'Choose the avatar, look and model, generate the script, then review before production.',
      complete: hasVideo,
      action: hasVideo ? 'View videos' : 'Create video',
      to: hasVideo ? { name: 'jobs' } : { name: 'new-job' },
      icon: Clapperboard,
      permission: canEdit,
    },
  ]

  const completed = steps.filter(step => step.complete).length
  const next = steps.findIndex(step => !step.complete)

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <header className="max-w-2xl">
        <p className="text-xs font-medium uppercase tracking-[0.1em] text-brand">Workspace setup</p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight">Getting started</h1>
        <p className="mt-2 text-sm leading-relaxed text-ink-faint">
          Complete these in order so generation has the credentials, product facts and visual references it needs.
        </p>
      </header>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-2 w-full" />
          {[0, 1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
      ) : (
        <>
          <section aria-label="Setup progress">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-ink">{completed} of {steps.length} complete</span>
              <span className="text-ink-faint">{Math.round((completed / steps.length) * 100)}%</span>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-3">
              <div
                className="h-full rounded-full bg-brand transition-[width] duration-500"
                style={{ width: `${(completed / steps.length) * 100}%` }}
              />
            </div>
          </section>

          <ErrorText error={error} />

          <section className="divide-y divide-line border-y border-line">
            {steps.map((step, index) => {
              const Icon = step.icon
              const active = index === next
              return (
                <div
                  key={step.title}
                  className={cn(
                    'group flex items-center gap-4 px-1 py-5 transition-colors hover:bg-surface-2/30 sm:px-3',
                    active && 'bg-brand/[0.035]',
                  )}
                >
                  <span className={cn(
                    'grid size-9 shrink-0 place-items-center rounded-full border transition-colors',
                    step.complete
                      ? 'border-good/30 bg-good/10 text-good'
                      : active
                        ? 'border-brand/35 bg-brand/10 text-brand'
                        : 'border-line bg-surface-2 text-ink-faint',
                  )}>
                    {step.complete ? <Check className="size-4" /> : <Icon className="size-4" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h2 className="text-sm font-medium">{step.title}</h2>
                      {active && <Circle className="size-1.5 fill-brand text-brand" />}
                    </div>
                    <p className="mt-1 text-xs leading-relaxed text-ink-faint">{step.description}</p>
                  </div>
                  {step.permission !== false && (
                    <Button
                      variant={active ? 'primary' : 'secondary'} size="sm"
                      onClick={() => go(step.to)} className="shrink-0"
                    >
                      {step.action} <ArrowRight />
                    </Button>
                  )}
                </div>
              )
            })}
          </section>

        </>
      )}
    </div>
  )
}
